import { DEFAULT_FORM_INPUT_TYPES, ITEM_STATUS_CODES, OPERATOR_RESPONSE_TYPES } from './constants';
import type { tInspectionFormInput, tPrompt } from '../types';

/** Stored values for Daily Service operator response patterns (non–inspect status codes). */
export const OPERATOR_RESPONSE_VALUES = {
    added: 'added',
    notAdded: 'notAdded',
    cleaned: 'cleaned',
    replaced: 'replaced',
    acknowledged: 'acknowledged',
    noNewIssues: 'noNewIssues',
} as const;

export type OperatorResponseValue =
    (typeof OPERATOR_RESPONSE_VALUES)[keyof typeof OPERATOR_RESPONSE_VALUES];

export type ResponseChoice = {
    value: string;
    label: string;
    /** Advance to next step immediately on select (non-flagging choices). */
    autoAdvance?: boolean;
};

/**
 * Prefer FormBuilder `operatorResponseType`, then legacy `type`.
 * Legacy `'question'` maps to the 4-button Good/Warn/Fail/N/A pattern.
 */
export function resolvePromptSubType(prompt: Pick<tPrompt, 'operatorResponseType' | 'type'>): string {
    const raw = prompt.operatorResponseType || prompt.type;
    if (!raw || raw === 'question') {
        return DEFAULT_FORM_INPUT_TYPES.fourResponse;
    }
    return raw;
}

/** Open issues use warn/inop as the inspect answer. DS quantity/choice steps do not. */
export function canPrefillPromptValueFromDiscrepancy(subType?: string): boolean {
    if (!subType) return true;
    return (
        subType === DEFAULT_FORM_INPUT_TYPES.fourResponse ||
        subType === DEFAULT_FORM_INPUT_TYPES.yesNo ||
        subType === OPERATOR_RESPONSE_TYPES.FOUR_BUTTON ||
        subType === OPERATOR_RESPONSE_TYPES.YES_NO
    );
}

export function mapSectionPrompt(sectionName: string, prompt: tPrompt): tInspectionFormInput {
    return {
        type: DEFAULT_FORM_INPUT_TYPES.section,
        title: sectionName,
        label: prompt.component,
        description: prompt.description,
        inputMode: prompt.inputMode?.value || 'text',
        subType: resolvePromptSubType(prompt),
        value: '',
        notes: '',
        photo: [],
        hasActiveDiscrepancy: false,
        discrepancyNotes: '',
        options: prompt.options || [],
        selectedOptions: [],
        category: prompt.category,
        defaultUM: prompt.defaultUM,
        partNumber: prompt.partNumber,
        partDescription: prompt.partDescription,
        vendor: prompt.vendor,
        autoFlag: prompt.autoFlag,
        serviceItemId: prompt.serviceItemId,
        serviceProcedureId: prompt.serviceProcedureId,
    };
}

/** Button choices for Daily Service operator response patterns. */
export function getOperatorResponseChoices(subType?: string): ResponseChoice[] | null {
    switch (subType) {
        case OPERATOR_RESPONSE_TYPES.ADDED_NOT_ADDED:
            return [
                { value: OPERATOR_RESPONSE_VALUES.added, label: 'Added', autoAdvance: true },
                { value: OPERATOR_RESPONSE_VALUES.notAdded, label: 'Not Added' },
            ];
        case OPERATOR_RESPONSE_TYPES.OK_CLEANED_REPLACED_NA:
            return [
                { value: ITEM_STATUS_CODES.ok, label: 'OK', autoAdvance: true },
                { value: OPERATOR_RESPONSE_VALUES.cleaned, label: 'Cleaned' },
                { value: OPERATOR_RESPONSE_VALUES.replaced, label: 'Replaced' },
                { value: ITEM_STATUS_CODES.na, label: 'N/A', autoAdvance: true },
            ];
        case OPERATOR_RESPONSE_TYPES.OK_REPLACED_NA:
            return [
                { value: ITEM_STATUS_CODES.ok, label: 'OK', autoAdvance: true },
                { value: OPERATOR_RESPONSE_VALUES.replaced, label: 'Replaced' },
                { value: ITEM_STATUS_CODES.na, label: 'N/A', autoAdvance: true },
            ];
        default:
            return null;
    }
}

/**
 * Whether this response should create/update an Issue (Discrepancy) on submit.
 * Always for warn/inop; additionally for autoFlag trigger values on Daily Service patterns.
 * Status Unchanged / under review acknowledge an open issue without creating a new flag.
 */
export function shouldCreateIssueForPrompt(
    prompt: Pick<tInspectionFormInput, 'value' | 'subType' | 'autoFlag'>,
): boolean {
    if (!prompt.value) return false;
    if (
        prompt.value === ITEM_STATUS_CODES.status_unchanged ||
        prompt.value === ITEM_STATUS_CODES.under_review
    ) {
        return false;
    }
    if (prompt.value === ITEM_STATUS_CODES.warn || prompt.value === ITEM_STATUS_CODES.inop) {
        return true;
    }
    if (!prompt.autoFlag) return false;
    return isAutoFlagTrigger(prompt.subType, prompt.value);
}

export function isEnteredQuantityValue(value: string | null | undefined): boolean {
    if (!value) return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    const amount = Number(trimmed);
    return Number.isFinite(amount) && amount > 0;
}

export function isAutoFlagTrigger(subType: string | undefined, value: string): boolean {
    if (!value) return false;

    switch (subType) {
        case OPERATOR_RESPONSE_TYPES.ADDED_NOT_ADDED:
            return value === OPERATOR_RESPONSE_VALUES.notAdded;
        case OPERATOR_RESPONSE_TYPES.OK_CLEANED_REPLACED_NA:
            return (
                value === OPERATOR_RESPONSE_VALUES.cleaned ||
                value === OPERATOR_RESPONSE_VALUES.replaced
            );
        case OPERATOR_RESPONSE_TYPES.OK_REPLACED_NA:
            return value === OPERATOR_RESPONSE_VALUES.replaced;
        case OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY:
            // Unscheduled consumables (autoFlag) email when a quantity is entered.
            return isEnteredQuantityValue(value);
        case OPERATOR_RESPONSE_TYPES.ACKNOWLEDGEMENT:
            return false;
        case OPERATOR_RESPONSE_TYPES.NEW_ISSUES:
            return value === ITEM_STATUS_CODES.warn;
        case OPERATOR_RESPONSE_TYPES.YES_NO:
        case DEFAULT_FORM_INPUT_TYPES.yesNo:
            return value === ITEM_STATUS_CODES.warn;
        case OPERATOR_RESPONSE_TYPES.FOUR_BUTTON:
        case DEFAULT_FORM_INPUT_TYPES.fourResponse:
            return value === ITEM_STATUS_CODES.warn || value === ITEM_STATUS_CODES.inop;
        default:
            return false;
    }
}

/** Human-readable label for review / status display. */
export function formatOperatorResponseLabel(value: string): string {
    switch (value) {
        case OPERATOR_RESPONSE_VALUES.added:
            return 'Added';
        case OPERATOR_RESPONSE_VALUES.notAdded:
            return 'Not Added';
        case OPERATOR_RESPONSE_VALUES.cleaned:
            return 'Cleaned';
        case OPERATOR_RESPONSE_VALUES.replaced:
            return 'Replaced';
        case OPERATOR_RESPONSE_VALUES.acknowledged:
            return 'Completed';
        case OPERATOR_RESPONSE_VALUES.noNewIssues:
            return 'No new issues';
        default:
            return value;
    }
}

/** Require notes or media before continuing (warn/inop or autoFlag trigger). */
export function requiresNotesOrMedia(
    prompt: Pick<tInspectionFormInput, 'value' | 'subType' | 'autoFlag'>,
): boolean {
    if (!prompt.value) return false;
    if (prompt.subType === OPERATOR_RESPONSE_TYPES.QUANTITY_ONLY) {
        return false;
    }
    if (prompt.value === ITEM_STATUS_CODES.warn || prompt.value === ITEM_STATUS_CODES.inop) {
        return true;
    }
    return Boolean(prompt.autoFlag && isAutoFlagTrigger(prompt.subType, prompt.value));
}
